import { useMemo } from "react";
import { compact } from "lodash";
import { FullScreenModal } from "@/react/components/shared/ModalsV2/FullScreenModal";
import type { PaywallStatus } from "@/react/types";
import { shouldDisplayWorkflowsTab } from "@circle-react/helpers/paywallHelpers";
import { useIsPaywallUpsellsEnabled } from "@circle-react/hooks/useIsPaywallUpsellsEnabled";
import { tabs } from "../../constants";
import { AccessTab } from "../AccessTab";
import { CheckoutTab } from "../CheckoutTab";
import { DetailsTab } from "../DetailsTab";
import { PostPurchaseTab } from "../PostPurchaseTab";
import { PricingTab } from "../PricingTab";
import { TrackingTab } from "../TrackingTab";
import { UpsellTab } from "../UpsellTab";
import { WorkflowsTab } from "../WorkflowsTab";
import type { TabOption } from "../types";
import { Header } from "./Header";
import { ModalFooter } from "./ModalFooter";
import type { usePaywallFormContentCreateFlow } from "./hooks/usePaywallFormContentCreateFlow";
import type { usePaywallFormContentEditFlow } from "./hooks/usePaywallFormContentEditFlow";

type UsePaywallFormContentHook =
  | typeof usePaywallFormContentCreateFlow
  | typeof usePaywallFormContentEditFlow;

interface PaywallFormContentProps {
  activeTab: string;
  canChangeCurrency: boolean;
  currentPaywallStatus?: PaywallStatus;
  hasSubscribers: boolean;
  isSubmitting: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we will fix this later when we convert the files to typescript
  mutation: any;
  requiresCurrency: boolean;
  setActiveTab: (tab: string) => void;
  usePaywallFormContentHook: UsePaywallFormContentHook;
  workflowsEnabled: boolean;
}

export const PaywallFormContent = ({
  activeTab,
  setActiveTab,
  usePaywallFormContentHook,
  mutation,
  isSubmitting,
  requiresCurrency,
  canChangeCurrency,
  currentPaywallStatus,
  workflowsEnabled,
  hasSubscribers,
}: PaywallFormContentProps) => {
  const { handleTabClick, tabOptions, shouldBlockSubmitButton } =
    usePaywallFormContentHook({ activeTab, setActiveTab });
  const isPaywallUpsellsEnabled = useIsPaywallUpsellsEnabled();

  const renderTab = (tab: string) => {
    const tabsToRender = {
      [tabs.DETAILS_TAB]: (
        <DetailsTab
          requiresCurrency={requiresCurrency}
          canChangeCurrency={canChangeCurrency}
        />
      ),
      [tabs.CHECKOUT_TAB]: <CheckoutTab />,
      [tabs.UPSELL_TAB]: <UpsellTab />,
      [tabs.PRICING_TAB]: <PricingTab />,
      [tabs.ACCESS_TAB]: <AccessTab hasSubscribers={hasSubscribers} />,
      [tabs.POST_PURCHASE_TAB]: (
        <PostPurchaseTab
          shouldDisplayWorkflowsTab={shouldDisplayWorkflowsTab(
            currentPaywallStatus,
          )}
          setActiveTab={setActiveTab}
        />
      ),
      [tabs.TRACKING_TAB]: <TrackingTab />,
      [tabs.WORKFLOWS_TAB]: <WorkflowsTab />,
    };

    return tabsToRender[tab];
  };

  const shouldRemoveWorkflowsTab =
    !workflowsEnabled || !shouldDisplayWorkflowsTab(currentPaywallStatus);
  const shouldRemoveUpsellTab = !isPaywallUpsellsEnabled;
  const tabsToRemove = useMemo(
    () =>
      compact([
        shouldRemoveWorkflowsTab ? tabs.WORKFLOWS_TAB : null,
        shouldRemoveUpsellTab ? tabs.UPSELL_TAB : null,
      ]),
    [shouldRemoveWorkflowsTab, shouldRemoveUpsellTab],
  );

  const filteredTabOptions = useMemo(
    () =>
      shouldRemoveWorkflowsTab || shouldRemoveUpsellTab
        ? tabOptions.filter(
            (option: TabOption) => !tabsToRemove.includes(option.label),
          )
        : tabOptions,
    [tabOptions, shouldRemoveWorkflowsTab, shouldRemoveUpsellTab, tabsToRemove],
  );

  return (
    <>
      <FullScreenModal.Header className="z-10">
        <Header
          tabs={filteredTabOptions}
          onClickTab={handleTabClick}
          activeTab={activeTab}
          rightContainer={
            <ModalFooter
              isSubmitting={isSubmitting}
              mutation={mutation}
              activeTab={activeTab}
              shouldBlockSubmitButton={shouldBlockSubmitButton}
              currentPaywallStatus={currentPaywallStatus}
            />
          }
        />
      </FullScreenModal.Header>
      {renderTab(activeTab)}
      <div className="border-primary sticky border-t p-5 md:py-4 lg:hidden lg:px-6">
        <ModalFooter
          isSubmitting={isSubmitting}
          mutation={mutation}
          activeTab={activeTab}
          shouldBlockSubmitButton={shouldBlockSubmitButton}
          currentPaywallStatus={currentPaywallStatus}
        />
      </div>
    </>
  );
};
